import {
    handleRotate,
    handleFloat,
    handleDimension,
    handleStrokeWeight,
    Color,
    Base,
    generateId,
    handleBool,
    Lissajous,
    Rose
} from "../index.js"

export default class PixelObject extends Base {
    constructor(options) {
        super(options)

        const defaults = {
            type: 'PIXELOBJECT',
            rotate: 0,
            x: 0,
            y: 0,
            width: 0,
            height: 0,
            strokeWeight: 1,
            randomFill: false,
            randomStroke: false,
            children: [],
            skipParentGeneration: false,
            fills: [{
                type: 'solid',
                val: '#C4C4C4',
                color: {
                    r: 196,
                    g: 196,
                    b: 196
                }
            }],
            constraints: {
                horizontal: 'SCALE',
                vertical: 'SCALE'
            },
        }

        const opts = { ...defaults, ...options }

        let self = this

        this.skipParentGeneration = opts.skipParentGeneration

        this.type = 'PIXELOBECT'
        this.rotate = opts.rotate
        this.x = opts.x
        this.y = opts.y
        this.width = opts.width
        this.height = opts.height

        this.strokeWeight = opts.strokeWeight

        this.randomFill = opts.randomFill
        this.randomStroke = opts.randomStroke

        this.children = opts.children

        this.setColor('fills', opts.fills)
        this.setColor('strokes', opts.strokes)

        this.constraints = opts.constraints

        this.eventListeners = []

        this.children.splice = (function () {
            if (arguments[2]) {
                arguments[2].setParent(self)
            }

            Array.prototype.splice.apply(this, arguments);

            self.dispatch('objectStateChanged', self.propertyChangedMessage('Children changed'))
        })

        this.children.push = (function () {
            const list = new Array(...arguments)
            list.forEach(e => e.setParent (self))

            Array.prototype.push.apply(this, list);

            self.dispatch('objectStateChanged', self.propertyChangedMessage('Children changed'))
        })
    }

    setColor(key, colors) {
        if (colors && colors.constructor == Array && colors.length > 0) {
            return this[key] = colors.map((e) => new Color({ ...e, parent: this }))
        }
        this[key] = []
    }

    set(key, newValue) {
        let config = {
            rotate: handleRotate,
            x: handleFloat,
            y: handleFloat,
            width: handleDimension,
            height: handleDimension,
            strokeWeight: handleStrokeWeight,
            randomFill: handleBool,
            randomStroke: handleBool
        }

        if (config[key]) {
            
            const oldValue = this[key]
            
            let parsedValue = config[key]({ newValue, oldValue })
            if (oldValue != parsedValue) {
                this[key] = parsedValue
                this.dispatch('objectStateChanged', this.propertyChangedMessage(key))
            }
        } else {
            Base.prototype.set.call(this, key, newValue)
        }
    }

    get(key) {
        return Base.prototype.get.call(this, key)
    }

    // Instance methods

    getActiveChildren() {
        return this.children.filter(child => child.visible)
    }


    getCachedCopy(args) {
        if (!this.cachedCopy || args?.forcedUpdate) {
            this.cachedCopy = this.copy()
            if (this.cachedCopy.randomStroke) {
                this.cachedCopy.strokes = [this.ctx.random(this.strokes)]
            }
            return this.cachedCopy
        } else {
            return this.cachedCopy
        }
    }

    createBuffer(width, height) {
        width = width == undefined ? this.ctx.width : width
        height = height == undefined ? this.ctx.height : height
        const buffer = this.ctx.createGraphics(width, height)
        this.setBuffer(buffer)
        this.renderBuffer()
    }

    getObjectProperties() {
        const base = this.getBaseProperties()
        const object = {
            type: this.type,
            rotate: this.rotate,
            x: this.x,
            y: this.y,
            width: this.width,
            height: this.height,
            fills: this.getFillsProperties(),
            strokes: this.getStrokesProperties(),
            strokeWeight: this.strokeWeight,
            randomFill: this.randomFill,
            randomStroke: this.randomStroke,
            children: this.children,
        }

        return { ...base, ...object }
    }

    addChild(object) {
        this.children.push(object)
    }

    duplicate(parentElement) {
        let selfCopy = this.copy()
        selfCopy.attachRoot(this.root)
        selfCopy.root.flat.push(selfCopy)
        selfCopy.id = generateId()
        selfCopy.x = handleFloat(selfCopy.x) + 10
        selfCopy.y = handleFloat(selfCopy.y) + 10

        function recursive(selfCopy) {
            let tempArr = []
            for (let i = 0; i < selfCopy.children.length; i++) {
                const childChopy = selfCopy.children[i].copy();

                childChopy.attachRoot(selfCopy.root)
                childChopy.root.flat.push(childChopy)
                childChopy.id = generateId()
                recursive(childChopy)
                tempArr.push(childChopy)
            }
            selfCopy.children = tempArr
        }

        recursive(selfCopy)

        if (parentElement) {
            parentElement.addChild(selfCopy)
        } else {
            // If element is at root level
            this.root.addChild(selfCopy)
        }
    }

    setModifier({type}) {
        const classMapping = {
            'Lissajous': Lissajous,
            'Rose': Rose,
        };

        const object = new classMapping[type]({ parent: this })

        this.modifiers = []
        this.modifiers.push(object)
        this.dispatch('objectStateChanged', this.propertyChangedMessage('Modifier set'))
    }

    addFill() {
        this.fills.push(new Color({ parent: this }))
        this.dispatch('objectStateChanged', this.propertyChangedMessage('Fill added'))
    }

    addStroke() {
        this.strokes.push(new Color({ val: "#C4C4C4", parent: this }))
        this.dispatch('objectStateChanged', this.propertyChangedMessage('Stroke added'))
    }

    attachRoot(context) {
        this.root = context
    }

    static showBoundingBox(x1, y1, x2, y2, selected) {
        if (!selected) return
        this.ctx.push()
        this.ctx.noFill()
        this.ctx.strokeWeight(1)
        this.ctx.stroke(24, 160, 251)
        this.ctx.rect(x1, y1, x2, y2)
        this.ctx.pop()
    }

    position() {
        const x = this.getRenderPositionProperty('x') + this.getRenderPropertyValue('width') / 2
        const y = this.getRenderPositionProperty('y') + this.getRenderPropertyValue('height') / 2
        this.ctx.translate(x, y)
        this.ctx.rotate(this.ctx.radians(this.rotate))
    }

    getRenderPropertyValue(propertyName) {
        if (this.renderScaleOptions && this.renderScaleOptions[propertyName]) {
            return this[propertyName] * this.renderScaleOptions[propertyName]
        }
        return this[propertyName]
    }

    getRenderScaleForProperty(propertyName) {
        if (this.renderScaleOptions && this.renderScaleOptions[propertyName]) {
            return this.renderScaleOptions[propertyName]
        }
        return 1
    }

    setRenderScaleProperties(properties) {
        if (!this.renderScaleOptions) {
            this.renderScaleOptions = {}
        }
        if (properties) {
            this.renderScaleOptions = { ...this.renderScaleOptions, ...properties }
        }
    }

    getRenderPositionProperty(propertyName) {
        if (this.renderPositionOptions && this.renderPositionOptions[propertyName]) {
            return this.renderPositionOptions[propertyName]
        }
        return this[propertyName]
    }

    setRenderPositionProperties(properties) {
        if (!this.renderPositionOptions) {
            this.renderPositionOptions = {}
        }
        if (properties) {
            this.renderPositionOptions = { ...this.renderPositionOptions, ...properties }
        }
    }

    setRenderPositionBasedOnScaleProperties(scaleProperties) {
        let x, y;
        if (this.x != 0 && this.width != 0) {
            x = this.x * scaleProperties.width
        }
        if (this.y != 0 && this.height != 0) {
            y = this.y * scaleProperties.height
        }
        this.setRenderPositionProperties({x,y})
    }

    addBits(newVal, oldVal) {
        try {
            return eval(newVal)
        } catch (e) {
            return oldVal
        }
    }

    getActiveFills() {
        return this.fills.filter(fill => !(fill.opacity == '0' || fill.deleted || !fill.visible))
    }

    getActiveStrokes() {
        return this.strokes.filter(stroke => !(stroke.opacity == '0' || stroke.deleted || !stroke.visible))
    }

    fill() {
        if (!this.fills) return
        const fills = this.getActiveFills()

        if (!fills.length) {
            return
        }

        if (this.randomFill) {
            // select random fill
            let rgb = this.ctx.random(fills)
            this.ctx.fill(rgb.color.r, rgb.color.g, rgb.color.b, this.opacity / 100 * rgb.opacity)
        } else {
            if (fills.length == 1) {
                // If there is only one fill
                let rgb = fills[0].color
                this.ctx.fill(rgb.r, rgb.g, rgb.b, this.opacity / 100 * fills[0].opacity)
            } else {
                let firstColor = fills[fills.length - 1]
                let mixedColor;
                let colorOpacity;

                for (var i = fills.length - 2; i >= 0; i--) {
                    const color = fills[i];
                    let mix = firstColor.mixRgbColors(color)
                    colorOpacity = mix.alpha
                    mixedColor = Color.convertRbgArrayToObject(mix.color)
                }

                let rgb = mixedColor
                this.ctx.fill(rgb.r, rgb.g, rgb.b, this.opacity * colorOpacity)
            }
        }
    }

    stroke() {
        if (!this.strokes) return
        const strokes = this.getActiveStrokes()

        if (!strokes.length || !this.strokeWeight) {
            this.ctx.noStroke()
            return
        }

        if (this.randomStroke) {
            // select random fill
            let rgb = this.ctx.random(strokes)
            this.ctx.stroke(rgb.color.r, rgb.color.g, rgb.color.b, this.opacity / 100 * rgb.opacity)
        } else {
            if (strokes.length == 1) {
                // If there is only one fill
                let rgb = strokes[0].color
                this.ctx.stroke(rgb.r, rgb.g, rgb.b, this.opacity / 100 * strokes[0].opacity)
            } else {
                let firstColor = strokes[strokes.length - 1]
                let mixedColor;
                let colorOpacity;

                for (var i = strokes.length - 2; i >= 0; i--) {
                    const color = strokes[i];
                    let mix = firstColor.mixRgbColors(color)
                    colorOpacity = mix.alpha
                    mixedColor = Color.convertRbgArrayToObject(mix.color)
                }

                let rgb = mixedColor
                this.ctx.stroke(rgb.r, rgb.g, rgb.b, this.opacity * colorOpacity)
            }
        }

        if (this.strokeWeight) {
            this.ctx.strokeWeight(this.strokeWeight)
        } else {
            this.ctx.noStroke()
        }
    }

    getBoundingBox() {
        return { width: this.width, height: this.height }
    }
}